import * as React from 'react';
import { Close } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getCookie, setCookie } from '@/redux/modules/cookies';
import { getMobileBrowserOS } from '../../redux/modules/browser';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useLocation } from 'react-router-dom';
import ButtonLink from '@liveauctioneers/caterwaul-components/lib/ButtonLink/ButtonLink';
import styled from 'styled-components';

type Props = {
    className?: string;
};

const COOKIE_MAX_AGE = 86400 * 30; // 30 days
const COOKIE_NAME = 'dismissableMobileBannerAlt';
const COOKIE_BODY = 'dismissed';

const DismissableGetMobileBannerAlt = ({ className }: Props) => {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();
    const mobileBrowserOS = useAppSelector(getMobileBrowserOS);
    const dismissed = useAppSelector((state) => getCookie(state, COOKIE_NAME)) === COOKIE_BODY;
    const isCheckoutPage = pathname.includes('/checkout/');
    const biddingConsole = pathname.includes('/console/');

    const setCookieChecked = React.useCallback((): void => {
        dispatch(
            setCookie({
                maxAge: COOKIE_MAX_AGE,
                name: COOKIE_NAME,
                value: COOKIE_BODY,
            })
        );
    }, [dispatch]);

    if (biddingConsole || isCheckoutPage || mobileBrowserOS === '' || dismissed) {
        return null;
    }

    const isIOS = mobileBrowserOS === 'ios';
    const navTo = isIOS
        ? 'https://itunes.apple.com/us/app/live-auctions/id321243082'
        : 'https://play.google.com/store/apps/details?id=com.liveauctioneers.and';

    return (
        <StyledMobileBanner
            className={className}
            id="mobileBannerAlt"
        >
            {isIOS ? (
                <StyledImage
                    alt="iphone"
                    src="https://p1.liveauctioneers.com/dist/images/iPhoneXs_Download_Home.png"
                />
            ) : (
                <StyledImage
                    alt="androidPhone"
                    src="https://p1.liveauctioneers.com/dist/images/Pixel3_Download_Home.png"
                />
            )}
            <StyledRightDiv>
                <StyledNotificationTextDiv>
                    <FormattedMessage id="pro_tip_track_all_treasures" />
                </StyledNotificationTextDiv>
                <StyledAppButton
                    external
                    url={navTo}
                >
                    <FormattedMessage id="get_app" />
                </StyledAppButton>
            </StyledRightDiv>
            <IconButton
                className="absolute right-xs top-xs z-10"
                icon={Close}
                iconStyle="bold"
                onClick={setCookieChecked}
                size="xs"
                variant="transparent"
            />
        </StyledMobileBanner>
    );
};

export default DismissableGetMobileBannerAlt;

const StyledMobileBanner = styled.div`
    display: flex;
    height: 120px;
    background-color: ${({ theme }) => theme.colors.grey500};
    align-items: center;
    justify-content: space-around;
    position: relative;
    padding-right: 21px;
    padding-left: 4px;
`;

const StyledImage = styled.img`
    max-height: 110px;
    align-self: flex-end;
`;

const StyledRightDiv = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-right: 8px;
    padding-bottom: 2px;

    a {
        text-decoration: none;
    }
`;

const StyledNotificationTextDiv = styled.div`
    text-align: center;
    max-width: 210px;
    font-family: ${({ theme }) => theme.typography.fontSecondary};
    line-height: 18px;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.black};
    font-size: 14px;
    margin-bottom: 10px;
`;

const StyledAppButton = styled(ButtonLink)`
    display: flex;
    width: 76px;
    height: 26px;
    background-color: ${({ theme }) => theme.colors.grey500};
    border: 1px solid ${({ theme }) => theme.colors.blue200};
    font-size: 12px;
    justify-content: center;
    padding: 0;
    line-height: normal;
    color: ${({ theme }) => theme.colors.blue200};
    align-items: center;
`;
